import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Check, Zap, Rocket, Crown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navigation from '../components/Navigation';

export default function PricingPage() {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Starter',
      price: '0',
      period: 'forever',
      icon: Zap,
      gradient: 'from-blue-500 to-cyan-500',
      features: [
        'Access to 50+ AI tools',
        'Basic marketplace features',
        '5 GB cloud storage',
        'Community support',
        'Standard auctions',
        'Email notifications'
      ],
      cta: 'Start Free',
      popular: false
    },
    {
      name: 'Pro',
      price: '29',
      period: 'month',
      icon: Rocket,
      gradient: 'from-purple-500 to-pink-500',
      features: [
        'Access to all 268+ AI tools',
        'Advanced marketplace features',
        '100 GB cloud storage',
        'Priority support',
        'Premium auctions',
        'Real-time notifications',
        'API access',
        'Custom integrations',
        'Voice UI features',
        'AI agent assistance'
      ],
      cta: 'Start Pro Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      icon: Crown,
      gradient: 'from-teal-500 to-cyan-500',
      features: [
        'Everything in Pro',
        'Unlimited AI tool access',
        'Unlimited cloud storage',
        'Dedicated account manager',
        'White-label solutions',
        'Custom AI model training',
        'SLA guarantees',
        'Advanced security',
        'Custom contracts',
        'Onboarding & training'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Animated Background */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-purple-950/20 to-black"></div>
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <Navigation />

      <div className="relative z-10 pt-24">
        {/* Hero Section */}
        <div className="py-32">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl md:text-7xl font-black mb-6">
                Simple, Transparent
                <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  Pricing
                </span>
              </h1>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
                Choose the perfect plan for your needs. Start free, upgrade anytime.
              </p>

              {/* Billing Toggle */}
              <div className="inline-flex items-center gap-4 p-2 bg-zinc-900 rounded-full border border-zinc-800">
                <button className="px-6 py-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold">
                  Monthly
                </button>
                <button className="px-6 py-2 rounded-full text-gray-400 hover:text-white transition-colors">
                  Annual
                  <span className="ml-2 text-xs text-cyan-400">(Save 20%)</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="pb-32">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-8">
              {plans.map((plan, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.2 }}
                  className={`relative p-8 rounded-3xl bg-zinc-900/50 border ${
                    plan.popular ? 'border-purple-500' : 'border-zinc-800'
                  } hover:border-purple-500/50 transition-all ${
                    plan.popular ? 'md:scale-105 md:shadow-2xl md:shadow-purple-500/20' : ''
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${plan.gradient} p-4 mb-6`}>
                    <plan.icon className="w-full h-full text-white" />
                  </div>

                  {/* Plan Name */}
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>

                  {/* Price */}
                  <div className="mb-6">
                    <span className="text-5xl font-black bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                      {plan.price === 'Custom' ? plan.price : `$${plan.price}`}
                    </span>
                    {plan.price !== 'Custom' && (
                      <span className="text-gray-500 ml-2">/{plan.period}</span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Button
                    onClick={() => navigate('/register')}
                    className={`w-full py-6 rounded-full font-semibold ${
                      plan.popular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                        : 'bg-zinc-800 hover:bg-zinc-700 text-white'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-20 border-t border-zinc-800">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-black mb-6">Frequently Asked Questions</h2>
            </motion.div>

            <div className="space-y-6">
              {[
                {
                  q: 'Can I switch plans anytime?',
                  a: 'Yes! You can upgrade, downgrade, or cancel your plan at any time. Changes take effect immediately.'
                },
                {
                  q: 'What payment methods do you accept?',
                  a: 'We accept all major credit cards through Stripe, including Visa, Mastercard, American Express, and more.'
                },
                {
                  q: 'Is there a free trial?',
                  a: 'Yes! Pro plans come with a 14-day free trial. No credit card required to start.'
                },
                {
                  q: 'What happens if I exceed my storage limit?',
                  a: 'You\'ll receive a notification when you\'re near your limit. You can upgrade anytime or purchase additional storage.'
                }
              ].map((faq, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800"
                >
                  <h3 className="text-xl font-bold mb-3 text-white">{faq.q}</h3>
                  <p className="text-gray-400">{faq.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-20">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-purple-600 to-cyan-600 p-12 md:p-16 text-center"
            >
              <div className="absolute inset-0 bg-black/20"></div>
              <div className="relative z-10">
                <h2 className="text-4xl md:text-5xl font-black mb-6">
                  Still have questions?
                </h2>
                <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                  Our team is here to help you find the perfect plan
                </p>
                <Button
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-black hover:bg-zinc-900 text-white px-10 py-6 text-lg rounded-full"
                >
                  Contact Sales
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}